import { createDefaultSettings as createDefaultSettingsShape } from '../settings/default-settings.js';
/**
 * sync-projects-state.js
 * Normalises project collections, persists settings, updates global snapshots,
 * and broadcasts change notifications for legacy + modular consumers.
 */

export function createSyncProjectsState({
  loadSettings = defaultLoadSettings,
  saveSettings = defaultSaveSettings,
  setLastProjects = defaultSetLastProjects,
  dispatchProjectsUpdated = null,
  defaultSource = 'projects-service',
  cloneProject = defaultCloneProject,
} = {}) {
  return function syncProjectsState(projects, options = {}) {
    const list = Array.isArray(projects) ? projects : [];
    const rawProjects = list
      .map((project) => {
        try {
          return cloneProject(project);
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean);

    trySetLastProjects(setLastProjects, rawProjects);

    const settingsSnapshot = safeNormalize(loadSettings);
    const hasExplicitActiveId = Object.prototype.hasOwnProperty.call(options, 'activeTeamId');

    let resolvedActiveId = hasExplicitActiveId
      ? normalizeActiveTeamId(options.activeTeamId)
      : inferActiveTeamId(settingsSnapshot, rawProjects);

    if (typeof resolvedActiveId === 'undefined') {
      resolvedActiveId = null;
    }

    const nextSettings = {
      ...settingsSnapshot,
      projects: cloneProjectList(rawProjects),
      activeTeamId: resolvedActiveId ?? null,
      last: {
        ...(settingsSnapshot.last || {}),
        teamId: resolvedActiveId ?? '',
      },
    };

    const persisted = safeSave(saveSettings, nextSettings);
    const finalSettings = normalizeSettings(persisted);

    const detail = buildEventDetail({
      rawProjects,
      source: options.source || defaultSource,
      viewModel: options.viewModel,
      activeTeamId: resolvedActiveId,
    });

    if (typeof dispatchProjectsUpdated === 'function') {
      try {
        dispatchProjectsUpdated(detail, finalSettings);
      } catch (dispatchError) {
        // Swallow dispatch errors to keep sync resilient.
      }
    }

    return {
      projects: cloneProjectList(rawProjects),
      settings: finalSettings,
      detail,
    };
  };
}

function defaultLoadSettings() {
  return normalizeSettings();
}

function defaultSaveSettings(next) {
  return normalizeSettings(next);
}

function defaultSetLastProjects(rawProjects) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.__lastProjects = rawProjects ? rawProjects.map((project) => ({ ...project })) : [];
  } catch (error) {
    // ignore window assignment errors
  }
}

function safeNormalize(loadSettings) {
  try {
    return normalizeSettings(loadSettings ? loadSettings() : undefined);
  } catch (error) {
    return normalizeSettings();
  }
}

function safeSave(saveSettings, settings) {
  try {
    const result = saveSettings ? saveSettings(settings) : settings;
    return normalizeSettings(result || settings);
  } catch (error) {
    return normalizeSettings(settings);
  }
}

function normalizeSettings(base = {}) {
  const defaults = createDefaultSettings();
  const normalized = {
    ...defaults,
    ...base,
    last: { ...defaults.last, ...(base.last || {}) },
    prefs: { ...defaults.prefs, ...(base.prefs || {}) },
  };
  normalized.projects = Array.isArray(base.projects) ? cloneProjectList(base.projects) : cloneProjectList(defaults.projects);
  normalized.teams = Array.isArray(base.teams) ? base.teams.slice() : defaults.teams.slice();
  normalized.library = Array.isArray(base.library) ? base.library.slice() : defaults.library.slice();
  if (!('tool' in normalized.last)) {
    normalized.last.tool = defaults.last.tool;
  }
  return normalized;
}

function createDefaultSettings() {
  return createDefaultSettingsShape();
}

function cloneProjectList(list = []) {
  return list.map((project) => ({ ...(project || {}) }));
}

function inferActiveTeamId(settings, projects) {
  const existingActive = normalizeActiveTeamId(settings?.activeTeamId);
  if (existingActive) {
    return existingActive;
  }
  const lastProject = normalizeActiveTeamId(settings?.last?.teamId);
  if (lastProject) {
    return lastProject;
  }
  const firstProject = normalizeActiveTeamId(projects?.[0]?.id ?? projects?.[0]?.project_id);
  if (firstProject) {
    return firstProject;
  }
  return null;
}

function normalizeActiveTeamId(value) {
  if (typeof value === 'undefined') {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function defaultCloneProject(project) {
  if (!project || typeof project !== 'object') {
    return null;
  }
  const raw = project.raw && typeof project.raw === 'object' ? project.raw : project;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const clone = { ...raw };
  const fallbackFields = [
    'id',
    'name',
    'project_id',
    'teamId',
    'team',
    'teamId',
    'team_id',
    'teamName',
    'description',
    'slug',
  ];
  for (const field of fallbackFields) {
    const current = clone[field];
    const candidate = project[field];
    if ((current === undefined || current === null || current === '') && (candidate !== undefined && candidate !== null && candidate !== '')) {
      clone[field] = candidate;
    }
  }
  if (clone.id === undefined || clone.id === null || clone.id === '') {
    const fallbackIdCandidates = [project.id, project.teamId, project.project_id];
    for (const value of fallbackIdCandidates) {
      if (value !== undefined && value !== null && value !== '') {
        clone.id = typeof value === 'number' ? String(value) : value;
        break;
      }
    }
  }
  return clone;
}

function trySetLastProjects(setter, projects) {
  if (typeof setter !== 'function') {
    return;
  }
  try {
    setter(projects);
  } catch (error) {
    // ignore setter failures to keep sync resilient
  }
}

function buildEventDetail({ rawProjects, source, viewModel, activeTeamId }) {
  const detail = {
    items: cloneProjectList(rawProjects),
    source,
  };
  if (typeof activeTeamId !== 'undefined') {
    detail.activeTeamId = activeTeamId ?? null;
  }
  if (typeof viewModel !== 'undefined') {
    detail.viewModel = viewModel;
  }
  return detail;
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.projects = window.AgileProDomain.projects || {};
  window.AgileProDomain.projects.createSyncProjectsState = createSyncProjectsState;
}

